/**
 * Client-only synthetic progress tool shown while the user stops an assistant response.
 */

import {ProgressData, ProgressToolCall, ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {
  CHAT_INTERRUPT_PROGRESS_DISPLAY_NAME_ATTEMPTING,
  CHAT_INTERRUPT_PROGRESS_DISPLAY_NAME_REQUESTING,
  CHAT_INTERRUPT_PROGRESS_DISPLAY_NAME_STOPPED,
  ToolType,
} from 'llm/chat-library/tool-details/types';

/** Synthetic tool name; must stay in sync with {@link ToolType.ChatInterruptProgress}. */
export const CHAT_INTERRUPT_PROGRESS_TOOL = ToolType.ChatInterruptProgress;

export const CHAT_INTERRUPT_SYNTHETIC_TOOL_ID = 'synthetic-chat-interrupt';

export enum InterruptRequestState {
  NOT_REQUESTED = 'NOT_REQUESTED',
  REQUESTING = 'REQUESTING',
  REQUESTED = 'REQUESTED',
}

export type InterruptProgressPhase = 'requesting' | 'attempting' | 'stopped';

export interface InterruptProgressInputs {
  interruptRequestState: InterruptRequestState;
  hasInterruptRequestedEvent: boolean;
  hasInterruptEvent: boolean;
  isThreadActive: boolean;
}

/**
 * Synthetic interrupt row phase for the thinking tool list.
 * Stays on "attempting" until the chat thread leaves ACTIVE/QUEUED (tools may still be finishing).
 */
export function getEffectiveInterruptProgressPhase(
  inputs: InterruptProgressInputs
): InterruptProgressPhase | null {
  const isInterruptRequested =
    inputs.interruptRequestState !== InterruptRequestState.NOT_REQUESTED || inputs.hasInterruptRequestedEvent;

  if (!isInterruptRequested) {
    return null;
  }

  if (inputs.interruptRequestState === InterruptRequestState.REQUESTING) {
    return 'requesting';
  }

  if (inputs.isThreadActive && !inputs.hasInterruptEvent) {
    return 'attempting';
  }

  return 'stopped';
}

/** True while the agent is still stopping (HTTP in flight or thread still ACTIVE/QUEUED). */
export function isInterruptStopInProgress(inputs: InterruptProgressInputs): boolean {
  const phase = getEffectiveInterruptProgressPhase(inputs);
  return phase === 'requesting' || phase === 'attempting';
}

export function createSyntheticInterruptProgressToolCall(phase: InterruptProgressPhase): ProgressToolCall {
  const status =
    phase === 'requesting'
      ? ToolCallStatus.QUEUED
      : phase === 'attempting'
        ? ToolCallStatus.RUNNING
        : ToolCallStatus.COMPLETED;
  const displayName =
    phase === 'requesting'
      ? CHAT_INTERRUPT_PROGRESS_DISPLAY_NAME_REQUESTING
      : phase === 'attempting'
        ? CHAT_INTERRUPT_PROGRESS_DISPLAY_NAME_ATTEMPTING
        : CHAT_INTERRUPT_PROGRESS_DISPLAY_NAME_STOPPED;
  return {
    tool: CHAT_INTERRUPT_PROGRESS_TOOL,
    toolId: CHAT_INTERRUPT_SYNTHETIC_TOOL_ID,
    createdAt: new Date(),
    status,
    displayName,
  };
}

export function appendSyntheticInterruptProgressToolCall(
  progressData: ProgressData | null,
  phase: InterruptProgressPhase
): ProgressData {
  const existingToolCalls = progressData?.toolCalls ?? [];
  const withoutPriorInterrupt = existingToolCalls.filter(
    (toolCall) => toolCall.toolId !== CHAT_INTERRUPT_SYNTHETIC_TOOL_ID
  );
  return {
    toolCalls: [...withoutPriorInterrupt, createSyntheticInterruptProgressToolCall(phase)],
  };
}

/** Tool calls from the current exchange, excluding the client-only interrupt progress row. */
export function getNonSyntheticProgressToolCalls(progressData: ProgressData | null): Array<ProgressToolCall> {
  return (progressData?.toolCalls ?? []).filter(
    (toolCall) => toolCall.toolId !== CHAT_INTERRUPT_SYNTHETIC_TOOL_ID
  );
}
